import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, Trophy, Timer, GraduationCap, Ticket, Check, Play, Video, Globe } from 'lucide-react';
import ProjectIcon from './ProjectIcon';
import './ProjectScrollReveal.css';

const tones = ['yellow', 'blue', 'green', 'purple'];

// Small illustrative mock screens, one per `art` key in data/projects.js
const arts = {
  learning: (
    <>
      <div className="pc-mock">
        <div className="pc-video">
          <span className="pc-live"><i /> LIVE</span>
          <span className="pc-play"><Play size={16} /></span>
        </div>
        {[['Lecture 12: Motion', 'Video'], ['Chapter notes', 'PDF'], ['Practice quiz', '10 Qs']].map(([t, k]) => (
          <div key={t} className="pc-sched pc-lesson"><span>{t}</span><small>{k}</small></div>
        ))}
      </div>
      <div className="pc-pill"><Video size={14} /> Live &amp; recorded</div>
    </>
  ),
  portal: (
    <>
      <div className="pc-mock">
        <div className="pc-mock-head">
          <span className="pc-mock-title">Mock Test 3</span>
          <span className="pc-score">82%</span>
        </div>
        <span className="pc-bar"><i style={{ width: '82%' }} /></span>
        <div className="pc-batch">
          <div><strong>Foundation Batch</strong><small>Tests, notes &amp; more</small></div>
          <span className="pc-enroll">Enroll</span>
        </div>
      </div>
      <div className="pc-pill"><Globe size={14} /> Student portal</div>
    </>
  ),
  quiz: (
    <>
      <div className="pc-mock">
        <div className="pc-mock-title">Live leaderboard</div>
        {[['1', 'Aarav', 96], ['2', 'Diya', 88], ['3', 'Kabir', 79]].map(([r, n, w]) => (
          <div key={r} className="pc-row">
            <span className={`pc-rank r${r}`}>{r}</span>
            <span className="pc-name">{n}</span>
            <span className="pc-bar"><i style={{ width: `${w}%` }} /></span>
          </div>
        ))}
      </div>
      <div className="pc-pill"><Trophy size={14} /> Auto-graded</div>
    </>
  ),
  exam: (
    <>
      <div className="pc-mock">
        <div className="pc-mock-head">
          <span className="pc-mock-title">Question 12 / 40</span>
          <span className="pc-timer"><Timer size={12} /> 12:45</span>
        </div>
        <div className="pc-skel w90" /><div className="pc-skel w60" />
        {['A', 'B', 'C'].map((o, i) => (
          <div key={o} className={`pc-opt ${i === 1 ? 'on' : ''}`}><b>{o}</b><span className="pc-skel w50" /></div>
        ))}
      </div>
      <div className="pc-pill"><Check size={14} /> Secure test mode</div>
    </>
  ),
  erp: (
    <>
      <div className="pc-mock">
        <div className="pc-mock-title">Campus overview</div>
        <div className="pc-tiles">
          {['Admissions', 'Attendance', 'Fees'].map((t, i) => (
            <div key={t} className="pc-tile"><small>{t}</small><span className="pc-bar"><i style={{ width: `${[72, 88, 55][i]}%` }} /></span></div>
          ))}
        </div>
        <div className="pc-chart">
          {[40, 65, 50, 80, 60, 90, 70].map((h, i) => <i key={i} style={{ height: `${h}%` }} />)}
        </div>
      </div>
      <div className="pc-pill"><GraduationCap size={14} /> All-in-one</div>
    </>
  ),
  events: (
    <>
      <div className="pc-mock">
        <div className="pc-ticket">
          <div>
            <small>HACKATHON</small>
            <strong>Innovate 2026</strong>
          </div>
          <span className="pc-qr" />
        </div>
        {['10:00  Registration', '11:00  Kick-off', '17:00  Demos'].map((r) => (
          <div key={r} className="pc-sched">{r}</div>
        ))}
      </div>
      <div className="pc-pill"><Ticket size={14} /> Certificates ready</div>
    </>
  )
};

// Move the spotlight highlight to follow the cursor over a card
const trackPointer = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
};

const ProjectCard = ({ project, index = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-40px' }}
    transition={{ duration: 0.45, delay: (index % 3) * 0.08 }}
    style={{ height: '100%' }}
  >
    <Link
      to={`/products/${project.slug}`}
      className={`project-card tone-${tones[index % tones.length]}`}
      onMouseMove={trackPointer}
    >
      <span className="project-open"><ArrowUpRight size={18} /></span>

      <div className="pc-text">
        <span className="project-category">{project.category}</span>
        <h3 className="project-name">{project.name}</h3>
        <p className="project-desc">{project.short}</p>
      </div>

      <div className="pc-art">
        {arts[project.art] || (
          <div className="pc-fallback"><ProjectIcon name={project.icon} size={44} /></div>
        )}
      </div>
    </Link>
  </motion.div>
);

export default ProjectCard;
